import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface ComboboxItem<T extends string> {
  value: T;
  label: string;
  hint?: string;
  icon?: ReactNode;
  badge?: ReactNode;
  searchAliases?: string[];
}

interface Props<T extends string> {
  value: T;
  onChange: (value: T) => void;
  items: ComboboxItem<T>[];
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  label?: string;
  hideSearch?: boolean;
  align?: 'left' | 'right';
}

export function Combobox<T extends string>({
  value, onChange, items, placeholder, searchPlaceholder, disabled, className,
  hideSearch, align = 'left',
}: Props<T>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const selected = useMemo(() => items.find((i) => i.value === value), [items, value]);
  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase().trim();
    return items.filter((i) => {
      const hay = [i.label, i.hint ?? '', i.value, ...(i.searchAliases ?? [])].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [items, query]);

  useEffect(() => {
    if (!open) return;
    setActiveIdx(Math.max(0, filtered.findIndex((i) => i.value === value)));
    setTimeout(() => searchRef.current?.focus(), 30);
    const onDown = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, filtered.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
      if (e.key === 'Enter')     {
        e.preventDefault();
        const it = filtered[activeIdx];
        if (it) { onChange(it.value); setOpen(false); }
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, filtered, value, onChange, activeIdx]);

  useEffect(() => {
    const el = listRef.current?.children[activeIdx] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  const showSearch = !hideSearch && items.length > 6;

  return (
    <div className={`relative ${className ?? ''}`} ref={popoverRef}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={[
          'w-full h-10 inline-flex items-center gap-2 rounded-lg border px-3 text-sm font-medium outline-none transition',
          'border-ink-200 bg-white text-ink-900 hover:border-ink-300',
          'dark:bg-ink-800/60 dark:border-ink-700 dark:text-ink-100 dark:hover:border-ink-600',
          'focus:ring-2 focus:ring-accent-500/30 focus:border-accent-500',
          open ? 'border-accent-500 ring-2 ring-accent-500/30' : '',
          'disabled:opacity-60 disabled:pointer-events-none',
        ].join(' ')}
      >
        {selected ? (
          <>
            {selected.icon && <span className="text-base shrink-0">{selected.icon}</span>}
            <span className="truncate text-left flex-1">{selected.label}</span>
            {selected.badge && <span className="text-[10px] text-ink-500 dark:text-ink-400 shrink-0">{selected.badge}</span>}
          </>
        ) : (
          <span className="truncate text-ink-500 dark:text-ink-400 font-normal">
            {placeholder ?? t('common.select', { defaultValue: 'Select…' })}
          </span>
        )}
        <ChevronDown className="h-4 w-4 text-ink-400 shrink-0 ml-auto" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            role="listbox"
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className={[
              'absolute z-50 top-11 w-full min-w-[280px] max-h-80 overflow-hidden flex flex-col',
              'rounded-xl border shadow-card',
              'bg-white border-ink-100',
              'dark:bg-ink-800 dark:border-ink-700',
              align === 'right' ? 'right-0' : 'left-0',
            ].join(' ')}
          >
            {showSearch && (
              <div className="p-2 border-b border-ink-100 dark:border-ink-700">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-400" />
                  <input
                    ref={searchRef}
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
                    placeholder={searchPlaceholder ?? t('common.searchEllipsis', { defaultValue: 'Search…' })}
                    className="w-full h-8 rounded-lg border border-ink-200 bg-white pl-8 pr-2 text-xs text-ink-900 outline-none focus:ring-2 focus:ring-accent-500/30 focus:border-accent-500 dark:bg-ink-900 dark:border-ink-700 dark:text-ink-100"
                  />
                </div>
              </div>
            )}
            {filtered.length === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-ink-500 dark:text-ink-400">
                {t('common.noResults', { defaultValue: 'No matches' })}
              </p>
            ) : (
              <ul ref={listRef} className="overflow-y-auto py-1">
                {filtered.map((it, i) => {
                  const active = it.value === value;
                  const focused = i === activeIdx;
                  return (
                    <li key={it.value}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={active}
                        onMouseEnter={() => setActiveIdx(i)}
                        onClick={() => { onChange(it.value); setOpen(false); }}
                        className={[
                          'w-full flex items-center gap-3 px-3 py-2 text-[13.5px] text-left transition',
                          active
                            ? 'bg-accent-50 text-accent-700 dark:bg-accent-500/15 dark:text-accent-300'
                            : focused
                              ? 'bg-ink-50 text-ink-900 dark:bg-ink-700/40 dark:text-ink-50'
                              : 'text-ink-700 dark:text-ink-200',
                        ].join(' ')}
                      >
                        {it.icon && <span className="text-base shrink-0">{it.icon}</span>}
                        <span className="flex-1 min-w-0">
                          <span className={`block truncate ${active ? 'font-semibold' : ''}`}>{it.label}</span>
                          {it.hint && <span className="block text-[10px] text-ink-500 dark:text-ink-400 truncate">{it.hint}</span>}
                        </span>
                        {it.badge && <span className="text-[10px] text-ink-500 dark:text-ink-400 shrink-0">{it.badge}</span>}
                        {active && <Check className="h-3.5 w-3.5 text-accent-600 dark:text-accent-400 shrink-0" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
